import { Stack, SvgIcon, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { ReactComponent as KeyIcon } from "@/assets/icons/dataPages/inventory/key.svg";
import { IKeyState, IKmsEventByKiosk } from "@/DataPages/types/inventory/models/inventory";

// Icons
import { ReactComponent as CarIcon } from "@/assets/icons/dataPages/inventory/car.svg";
import { ReactComponent as PersonIcon } from "@/assets/icons/dataPages/inventory/person.svg";

interface UrgentActionsGridCellProps {
	keyStates: IKeyState[];
	kmsLastEventsByKiosk: IKmsEventByKiosk[];
}

interface IKeyContent {
	key: string;
	state?: string;
	penaltyUserName?: string;
	eventType?: string;
	isConsistent?: boolean;
}

const renderKeyRow = (keyContent: IKeyContent) => {
	return (
		<Stack key={keyContent.key} display="flex" direction="row" justifyContent="flex-start" gap={1}>
			<Typography className="!font-sans !text-sm !font-normal !text-white">{keyContent.key}:</Typography>
			<Stack display="flex" direction="column">
				{keyContent.state && (
					<Stack display="flex" direction="row" justifyContent="flex-start" alignItems="center" gap={1}>
						<SvgIcon className="pl-[4px] pt-[2px] !w-[24px] !h-[24px] !fill-white" component={CarIcon} />
						<Typography className="!font-sans !text-sm !font-normal !text-white">
							Inconsistent State ({keyContent.state})
						</Typography>
					</Stack>
				)}
				{keyContent.penaltyUserName && (
					<Stack display="flex" direction="row" justifyContent="flex-start" alignItems="center">
						<SvgIcon className="pl-[4px] pt-[6px] !w-[32px] !h-[32px] !fill-white" component={PersonIcon} />
						<Typography className="!-mt-2 !font-sans !text-sm !font-normal !text-white">
							{keyContent.penaltyUserName} {keyContent.eventType && `(${keyContent.eventType})`}
						</Typography>
					</Stack>
				)}
			</Stack>
		</Stack>
	);
};

const getTooltipContent = (keyStates: IKeyState[], kmsLastEventsByKiosk: IKmsEventByKiosk[]) => {
	const maxLength = Math.max(keyStates.length, kmsLastEventsByKiosk.length);
	const keyContentArray = Array.from(
		{ length: maxLength },
		(_, index) =>
			({
				key: `Key ${index + 1}`,
				isConsistent: keyStates[index]?.isConsistent ?? false,
				state: keyStates[index]?.lastEvent?.replaceAll("_", " "),
				penaltyUserName: kmsLastEventsByKiosk[index]?.penaltyUserName,
				eventType: kmsLastEventsByKiosk[index]?.eventType?.replaceAll("_", " "),
			} as IKeyContent)
	);

	return <>{keyContentArray.map(renderKeyRow)}</>;
};

export const UrgentActionsGridCell = (props: Readonly<UrgentActionsGridCellProps>) => {
	const { keyStates, kmsLastEventsByKiosk } = props;
	const haveInconsistentKeys = keyStates?.some((keyState) => !keyState.isConsistent);
	const havePenaltyUsers = kmsLastEventsByKiosk?.some((kmsEvent) => kmsEvent.penaltyUserName);
	if (!haveInconsistentKeys && !havePenaltyUsers) {
		return <GridActionsCellItem icon={<KeyIcon className={"fill-gray-text"} />} label="Key" className="textPrimary" color="inherit" />;
	}
	return (
		<Tooltip
			title={getTooltipContent(keyStates, kmsLastEventsByKiosk)}
			key="key"
			arrow
			placement="right-start"
			classes={{ tooltip: "!font-regular-sans" }}
			slotProps={{
				popper: {
					modifiers: [
						{
							name: "offset",
							options: {
								offset: [0, -10],
							},
						},
					],
				},
			}}
		>
			<GridActionsCellItem icon={<KeyIcon className={"fill-error"} />} label="Key" className="textPrimary" color="inherit" />
		</Tooltip>
	);
};
